import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { registerPushNotifications } from '@/utils/pushNotifications';
import { useAuthStore } from '@/stores/useAuthStore';
import { featureFlagsQueryKey } from '@/features/feature-flags'
import { invalidateDashboards } from '@/features/dashboard/hooks/useDashboardStats'
import { notifyUser } from '@/lib/sounds';
import { apiRoot } from '@/lib/tenantHost';

interface SocketContextState {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextState>({
  socket: null,
  isConnected: false,
});

// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its provider, imported by feature code.
export const useSocket = () => useContext(SocketContext);

/** Fields this provider reads from the `new_notification` socket event. */
interface NewNotificationPayload {
  title?: string;
  message?: string;
  notification?: { metadata?: { source?: string } };
}

interface SocketProviderProps {
  children: React.ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Subscribe to the id only, not the whole user object. A re-created user
  // object with identical contents must not tear down and rebuild the socket:
  // that churn is what turned a backend outage into an endless reconnect and
  // /users/me storm.
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // A KAAMCO support session gets no socket and no push registration: it would
  // mark the user online on the wallboard and sign the operator's browser up
  // for the user's notifications. The server refuses the socket anyway.
  const inSupportSession = useAuthStore((s) => Boolean(s.user?.supportSession));
  const queryClient = useQueryClient();

  useEffect(() => {
    // 1. Request Browser Notification Permission
    if (isAuthenticated && !inSupportSession && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        console.log('Notification permission:', permission);
        if (permission === 'granted') {
          registerPushNotifications();
        }
      });
    }

    // 2. Register for Web Push if already granted
    if (isAuthenticated && !inSupportSession && 'Notification' in window && Notification.permission === 'granted') {
      registerPushNotifications();
    }

    // 3. Socket Connection Logic
    if (!isAuthenticated || !userId || inSupportSession) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        // eslint-disable-next-line react-hooks/set-state-in-effect -- signed out: clear the socket this effect owns.
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Same-origin on a custom domain, where Caddy proxies /socket.io too.
    const apiUrl = apiRoot();
    let baseUrl: string;
    if (apiUrl) {
      try {
        const u = new URL(apiUrl, window.location.origin);
        baseUrl = `${u.protocol}//${u.host}`;
      } catch {
        console.error('Invalid VITE_API_URL, falling back to window origin:', apiUrl);
        baseUrl = window.location.origin;
      }
    } else {
      baseUrl = window.location.origin;
    }

    const newSocket = io(baseUrl, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      // Keep trying in the background: a fixed attempt limit leaves a tab
      // permanently stale after a brief Wi-Fi/VPN outage until it is reloaded.
      // The delay still backs off to 30s so a dead server is not hammered.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 750,
      reconnectionDelayMax: 30_000,
      randomizationFactor: 0.5,
      autoConnect: true,
    });

    // --- Live presence heartbeat -------------------------------------------
    // The server derives online/idle/offline from this beat, not from HTTP
    // polling. We send one every HEARTBEAT_MS while connected; `active` reports
    // whether the user has interacted recently so the server can tell apart a
    // present-and-working user (online) from a present-but-away one (idle).
    const HEARTBEAT_MS = 20_000;
    const ACTIVE_WINDOW_MS = 60_000;
    let lastActivityAt = Date.now();
    const markActivity = (): void => {
      lastActivityAt = Date.now();
    };
    const activityEvents: Array<keyof DocumentEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'wheel',
      'visibilitychange',
    ];
    activityEvents.forEach((event) =>
      document.addEventListener(event, markActivity, { passive: true })
    );

    const sendHeartbeat = (): void => {
      if (!newSocket.connected) return;
      const active =
        document.visibilityState === 'visible' && Date.now() - lastActivityAt < ACTIVE_WINDOW_MS;
      newSocket.emit('presence.heartbeat', { active });
    };
    const heartbeatTimer = window.setInterval(sendHeartbeat, HEARTBEAT_MS);
    const DASHBOARD_REFRESH_DEBOUNCE_MS = 3_000;
    let dashboardRefreshTimer: number | null = null;

    // The token refresh rotates the server-side sessionId, orphaning this
    // socket's presence binding. Reconnect so it re-handshakes under the new
    // session (see api/client.ts).
    const handleTokenRefreshed = (): void => {
      markActivity();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current.connect();
      }
    };
    window.addEventListener('auth:token-refreshed', handleTokenRefreshed);

    newSocket.on('connect', () => {
      // Anything that changed while this tab was disconnected was broadcast to
      // nobody. Re-resolving on every connect is what makes the socket a
      // reliable propagation path rather than a best-effort one.
      queryClient.invalidateQueries({ queryKey: featureFlagsQueryKey });
      console.log('Socket Connected:', newSocket.id);
      setIsConnected(true);
      markActivity();
      sendHeartbeat();
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket Disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.io.on('reconnect_error', (error) => {
      console.warn('Socket reconnect attempt failed:', error.message);
    });

    newSocket.on('new_notification', (data: NewNotificationPayload) => {
      console.log('New notification received:', data);

      // 1. Invalidate react-query cache to update badge count and list immediately
      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      // Channel and direct-message notifications belong in the bell, but a
      // toast/sound for every message would interrupt an active conversation.
      if (data?.notification?.metadata?.source === 'channel_message') return;

      // 2. In-app toast
      toast.info(data.title || 'New Notification', {
        description: data.message || 'You have a new update.',
      });

      // 3. Play the shared alert sound and show a browser notification when permitted.
      notifyUser({
        title: data.title || 'New Notification',
        body: data.message || 'New update received.',
      });
    });

    // Channels have their own per-channel delivery preferences, so they emit a
    // lightweight realtime signal instead of creating duplicate generic records.
    // The chat UI handles unread badges and its in-thread new-message indicator;
    // showing a global toast here interrupts an active conversation.
    newSocket.on('channel.notification', () => {
      // Only the channel list contains unread counters. Invalidating the broad
      // `['channels']` key would also refetch every open messages/members query.
      queryClient.invalidateQueries({ queryKey: ['channels', 'list'] });
    });

    // This reaches every open tab of every channel member, including tabs
    // where that channel is not currently selected.
    newSocket.on('channel.members.changed', (data: { channelId?: string }) => {
      if (!data?.channelId) return;
      queryClient.invalidateQueries({ queryKey: ['channels', 'members', data.channelId] });
      queryClient.invalidateQueries({ queryKey: ['channels', 'list'] });
    });

    newSocket.on('organization.chat-settings.changed', () => {
      queryClient.invalidateQueries({ queryKey: ['organization', 'current'] });
      queryClient.invalidateQueries({ queryKey: ['channels', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['channels', 'people'] });
    });

    // Generic data-change signal: BE writes broadcast `{ entity }` so the FE can
    // invalidate the right React Query caches without re-fetching everything.
    // Always treat as a signal, never as the source of truth — the queries
    // re-fetch from the API after invalidation.
    newSocket.on('data-changed', (data: { entity?: string }) => {
      const entity = data?.entity;
      if (!entity) return;
      switch (entity) {
        case 'shift-assignments':
          queryClient.invalidateQueries({ queryKey: ['shifts'] });
          queryClient.invalidateQueries({ queryKey: ['shifts-overview'] });
          queryClient.invalidateQueries({ queryKey: ['shift-assignments'] });
          queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview'] });
          queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview-me'] });
          queryClient.invalidateQueries({ queryKey: ['shifts-roster'] });
          queryClient.invalidateQueries({ queryKey: ['attendance', 'me', 'today'] });
          break;
        case 'feature-flags':
          // An operator published, unpublished or re-scoped a feature. Refetch
          // rather than trusting the payload: the event says something changed,
          // the server says what the answer is now.
          queryClient.invalidateQueries({ queryKey: featureFlagsQueryKey });
          break;
        case 'company-members':
          queryClient.invalidateQueries({ queryKey: ['companies'] });
          queryClient.invalidateQueries({ queryKey: ['shifts-roster'] });
          break;
        case 'time-corrections':
          queryClient.invalidateQueries({ queryKey: ['timeCorrection'] });
          break;
        case 'leaves':
          queryClient.invalidateQueries({ queryKey: ['leaves'] });
          queryClient.invalidateQueries({ queryKey: ['my-leaves'] });
          break;
        case 'announcements':
          queryClient.invalidateQueries({ queryKey: ['announcements'] });
          break;
        case 'attendance':
          // Any punch, break, correction, auto clock-out or leave approval.
          queryClient.invalidateQueries({ queryKey: ['attendance'] });
          // Every punch in the org arrives here; a shift start is a burst.
          // Refresh the dashboards once the burst settles.
          if (dashboardRefreshTimer == null) {
            dashboardRefreshTimer = window.setTimeout(() => {
              dashboardRefreshTimer = null;
              void invalidateDashboards(queryClient);
            }, DASHBOARD_REFRESH_DEBOUNCE_MS);
          }
          break;
      }
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      window.clearInterval(heartbeatTimer);
      if (dashboardRefreshTimer != null) window.clearTimeout(dashboardRefreshTimer);
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed);
      activityEvents.forEach((event) => document.removeEventListener(event, markActivity));
      newSocket.disconnect();
      newSocket.io.off('reconnect_error');
      socketRef.current = null;
    };
  }, [isAuthenticated, userId, inSupportSession]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>{children}</SocketContext.Provider>
  );
}
